import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import * as React from 'react';
import { HiOutlineSearch, HiSelector } from 'react-icons/hi';
import * as PhoneInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import fr from 'react-phone-number-input/locale/fr.json';

export interface ResponsePhoneFieldProps {
  value?: string;
  label: string;
  error?: boolean;
  defaultCountry?: PhoneInput.Country;
  onChange?: (value: string) => void;
  onBlur?: () => void;
}

export const ResponsePhoneField = ({
  value,
  label,
  error,
  defaultCountry = 'FR',
  onChange,
  onBlur,
}: ResponsePhoneFieldProps) => {
  const hasValue = !!value?.trim();

  return (
    <div className="flex w-full flex-col gap-1">
      <div
        data-slot="phone-input"
        className={cn(
          'flex h-12 w-full items-stretch overflow-hidden rounded-1.5xl border pr-2 transition-colors',
          'border-gray-200 bg-white text-indigo-900',
          hasValue && '!bg-blue-50 font-medium',
          error && '!border-red-300 !bg-red-25',
          'focus-within:!border-indigo-900 focus-within:!bg-white',
        )}
      >
        <PhoneInput.default
          className="flex w-full items-stretch"
          labels={fr}
          defaultCountry={defaultCountry}
          flagComponent={FlagComponent}
          countrySelectComponent={CountrySelect}
          inputComponent={InputComponent}
          smartCaret={false}
          placeholder={label}
          aria-invalid={error}
          value={value || undefined}
          onChange={(next) => onChange?.(next || '')}
          onBlur={onBlur}
        />
      </div>
    </div>
  );
};

const InputComponent = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'>
>(({ className, ...props }, ref) => (
  <input
    ref={ref}
    className={cn(
      'h-full w-full min-w-0 flex-1 bg-transparent pl-1 text-base text-indigo-900 outline-none placeholder:text-gray-400',
      className,
    )}
    {...props}
  />
));
InputComponent.displayName = 'InputComponent';

type CountryEntry = { label: string; value: PhoneInput.Country | undefined };

type CountrySelectProps = {
  disabled?: boolean;
  value: PhoneInput.Country;
  options: CountryEntry[];
  onChange: (country: PhoneInput.Country) => void;
};

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
}: CountrySelectProps) => {
  const [searchValue, setSearchValue] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);

  const filteredCountries = countryList.filter(
    ({ value, label }) =>
      value && label.toLowerCase().includes(searchValue.toLowerCase()),
  );

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (open) setSearchValue('');
      }}
    >
      <PopoverTrigger
        render={
          <button
            type="button"
            className="flex items-center gap-1 self-stretch rounded-s-1.5xl border-r border-gray-200 pl-3 pr-2 transition-colors hover:cursor-pointer disabled:pointer-events-none disabled:opacity-50"
            disabled={disabled}
          />
        }
      >
        <FlagComponent
          country={selectedCountry}
          countryName={selectedCountry}
        />
        <HiSelector className={cn('size-4 opacity-50', disabled && 'hidden')} />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2">
          <HiOutlineSearch className="size-4 shrink-0 opacity-50" />
          <input
            className="h-8 w-full text-sm text-indigo-900 outline-none"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Rechercher un pays..."
          />
        </div>
        <div className="max-h-72 overflow-y-auto p-1">
          {filteredCountries.length === 0 && (
            <p className="px-2 py-1.5 text-sm text-gray-500">
              Aucun pays trouvé.
            </p>
          )}
          {filteredCountries.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-gray-100"
              onClick={() => {
                onChange(value!);
                requestAnimationFrame(() => setIsOpen(false));
              }}
            >
              <FlagComponent country={value!} countryName={label} />
              <span className="flex-1 text-left text-sm">{label}</span>
              <span className="text-sm text-gray-500">{`+${PhoneInput.getCountryCallingCode(
                value!,
              )}`}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const FlagComponent = ({ country, countryName }: PhoneInput.FlagProps) => {
  const Flag = flags[country];

  return (
    <span className="flex h-4 w-6 overflow-hidden rounded-sm [&_svg:not([class*='size-'])]:size-full">
      {Flag && <Flag title={countryName} />}
    </span>
  );
};
